import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import useInterfaceConfig from "@hooks/useInterfaceConfigV2";

interface LateralConfig {
  logo: string;
  logo_width: number | string;
  logo_height: number | string;
  bgColor: string;
  textColor: string;
  image: string;
  text: string;
}

interface LateralLogoProps {
  lateralConfig: LateralConfig | null;
}

interface LateralImageProps {
  lateralConfig?: LateralConfig | null;
}

const LateralLogo: React.FC<LateralLogoProps> = ({ lateralConfig }) => {
  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (!lateralConfig || hasError) {
    return null;
  }

  return (
    <>
      {isLogoLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: lateralConfig?.logo_width || 250,
            height: lateralConfig?.logo_height || 100,
          }}
        >
          <CircularProgress sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
        </Box>
      )}
      <img
        src={lateralConfig?.logo}
        alt="Logo"
        style={{
          width: lateralConfig?.logo_width || "100%",
          height: lateralConfig?.logo_height || "auto",
          display: isLogoLoading ? "none" : "block",
        }}
        onLoad={() => {
          setIsLogoLoading(false);
          setHasError(false);
        }}
        onError={() => {
          setHasError(true);
        }}
      />
    </>
  );
};

const LateralImage: React.FC<LateralImageProps> = ({ lateralConfig }) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (!lateralConfig || hasError) {
    return null;
  }

  return (
    <>
      {isImageLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: 300, height: 300 }}>
          <CircularProgress sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
        </Box>
      )}
      <img
        src={lateralConfig?.image}
        alt="Logo"
        style={{
          width: "100%",
          height: "auto",
          borderRadius: "10px",
          display: isImageLoading ? "none" : "block",
        }}
        onLoad={() => {
          setIsImageLoading(false);
        }}
        onError={() => {
          setHasError(true);
        }}
      />
    </>
  );
};

const CustomRemoteImage: React.FC = () => {
  const theme = useTheme();
  const interfaceConfig = useInterfaceConfig();

  const [lateralConfig, setLateralConfig] = React.useState<LateralConfig | null>(null);

  // generar config lateral aleatoria
  useEffect(() => {
    const {
      logo_bg_light,
      logo_bg_dark,
      logo_width,
      logo_height,
      bg_light,
      bg_dark,
      text_bg_light,
      text_bg_dark,
      image_list,
      text_list,
    } = interfaceConfig?.lateral || {};

    const lista = ["light", "dark"];
    const randomColorIndex = Math.floor(Math.random() * lista.length);
    const logo = lista[randomColorIndex] === "light" ? logo_bg_light : logo_bg_dark;
    const bgColor = lista[randomColorIndex] === "light" ? bg_light : bg_dark;
    const textColor = lista[randomColorIndex] === "light" ? text_bg_light : text_bg_dark;
    let randomImage = null;
    let randomText = null;

    // seleccionar imagen aleatoria
    if (image_list && image_list.length) {
      const randomIndex = Math.floor(Math.random() * image_list.length);
      randomImage = image_list[randomIndex];
    }

    // seleccionar texto aleatorio
    if (text_list && text_list.length) {
      const randomIndex = Math.floor(Math.random() * text_list.length);
      randomText = text_list[randomIndex];
    }

    setLateralConfig({
      logo: logo ? `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${logo}` : "",
      logo_width: logo_width || 200,
      logo_height: logo_height || 44,
      bgColor: bgColor || theme.palette.primary.main,
      textColor: textColor || theme.palette.primary.light,
      image: randomImage ? `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${randomImage}` : "",
      text: randomText || "",
    });
  }, [interfaceConfig, theme]);

  return (
    <Box
      sx={{
        margin: "auto",
        width: "100%",
        height: "100%",
        bgcolor: lateralConfig?.bgColor || "#FFFFFF",
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} height="100%">
        <Box sx={{ display: "flex", justifyContent: "center", width: "52%" }}>
          <LateralLogo lateralConfig={lateralConfig} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", width: "52%" }}>
          <LateralImage lateralConfig={lateralConfig} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", width: "52%", textAlign: "center" }}>
          <Typography
            fontSize={20}
            fontWeight={700}
            color={lateralConfig?.textColor}
            sx={{
              fontSize: { xs: 14, sm: 14, md: 14, lg: 18, xl: 22 },
            }}
          >
            {lateralConfig?.text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomRemoteImage;
